// ============================================================
//  SUPPORT REQUESTS BADGE — ADMIN DASHBOARD
//  Path: src/components/SupportRequestsBadge.tsx
//
//  NEW FILE (2026-10-10). Shown under a person's name on the Expert and
//  Customer detail pages, next to "Message in app". Tells staff whether this
//  person has help requests nobody has answered yet, without opening
//  Support:
//    - "💬 2 new support messages" (red) when there are open requests from
//      the app of this page — links to Support filtered to this person.
//    - "Support history" (grey) otherwise — links to all their requests.
//  appType matters for someone with both an expert and a customer account:
//  only requests sent from the app of the page you are on are counted.
// ============================================================

import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { getSupportOpenSummaryApi } from '../api/support.api'

const SupportRequestsBadge: React.FC<{
  userId: string | null | undefined
  appType: 'expert' | 'customer'
}> = ({ userId, appType }) => {
  const [open, setOpen] = useState<number | null>(null)

  useEffect(() => {
    if (!userId) return
    let alive = true
    getSupportOpenSummaryApi([userId])
      .then((res) => {
        const row = res.data?.data?.byUser?.[userId]
        if (alive) setOpen(Number(row?.[appType]) || 0)
      })
      .catch(() => { if (alive) setOpen(null) })
    return () => { alive = false }
  }, [userId, appType])

  if (!userId) return null
  const base = `/support?search=${encodeURIComponent(userId)}&appType=${appType}`

  if (open && open > 0) {
    return (
      <Link
        to={`${base}&status=open`}
        className="inline-flex items-center gap-1.5 mt-1.5 mr-2 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-500/15 text-red-300 border border-red-500/30 hover:bg-red-500/25 transition-colors"
        title="Help requests from this person that nobody has answered yet"
      >
        💬 {open} new support message{open === 1 ? '' : 's'}
      </Link>
    )
  }
  return (
    <Link to={`${base}&status=`} className="inline-flex items-center gap-1 mt-1.5 mr-2 text-xs text-gray-500 hover:text-gray-300 transition-colors">
      💬 Support history
    </Link>
  )
}

export default SupportRequestsBadge